import React, { useState } from 'react';
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';
import api from '../../services/api';

const EspacosModal = ({ isOpen, onClose, espacos, refreshData }) => {
  const [novoEspaco, setNovoEspaco] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');

  if (!isOpen) {
    return null;
  }

  const handleAdicionar = async (event) => {
    event.preventDefault();

    const nome = novoEspaco.trim();
    if (!nome) {
      return;
    }

    try {
      setSaving(true);
      await api.post('/agenda/espacos', { nome });
      setNovoEspaco('');
      refreshData();
    } catch (error) {
      const mensagem = error.response?.data?.detail || 'Erro ao adicionar espaço.';
      alert(mensagem);
    } finally {
      setSaving(false);
    }
  };

  const handleExcluir = async (espaco) => {
    if (!window.confirm(`Deseja realmente excluir o espaço "${espaco.nome}"?`)) {
      return;
    }

    try {
      await api.delete(`/agenda/espacos/${espaco.id}`);
      refreshData();
    } catch (error) {
      const mensagem = error.response?.data?.detail || 'Erro ao excluir espaço.';
      alert(mensagem);
    }
  };

  const handleIniciarEdicao = (espaco) => {
    setEditingId(espaco.id);
    setEditingName(espaco.nome);
  };

  const handleCancelarEdicao = () => {
    setEditingId(null);
    setEditingName('');
  };

  const handleRenomear = async (espaco) => {
    const nome = editingName.trim();
    if (!nome) {
      return;
    }

    try {
      setSaving(true);
      await api.put(`/agenda/espacos/${espaco.id}`, { nome });
      handleCancelarEdicao();
      refreshData();
    } catch (error) {
      const mensagem = error.response?.data?.detail || 'Erro ao renomear espaço.';
      alert(mensagem);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 bg-slate-50/60 px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-slate-800 p-2 text-white">
              <Pencil size={18} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">Editar espaços</h2>
              <p className="text-sm text-gray-500">Adicione ou remova áreas disponíveis na agenda</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 transition-colors hover:text-red-500">
            <X size={24} />
          </button>
        </div>

        <div className="space-y-5 p-6">
          <form onSubmit={handleAdicionar} className="flex gap-3">
            <input
              type="text"
              value={novoEspaco}
              onChange={(event) => setNovoEspaco(event.target.value)}
              placeholder="Nome do novo espaço"
              className="flex-1 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 outline-none transition-all focus:ring-2 focus:ring-slate-800"
            />
            <button
              type="submit"
              disabled={saving || !novoEspaco.trim()}
              className="flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-3 font-bold text-white transition-all hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
            >
              <Plus size={18} />
              Adicionar
            </button>
          </form>

          <div className="max-h-80 space-y-3 overflow-y-auto">
            {espacos.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-200 px-4 py-8 text-center text-sm italic text-gray-400">
                Nenhum espaço cadastrado.
              </div>
            ) : (
              espacos.map((espaco) => (
                <div
                  key={espaco.id}
                  className="flex items-center justify-between rounded-2xl border border-gray-100 bg-white px-4 py-4 shadow-sm"
                >
                  {editingId === espaco.id ? (
                    <>
                      <input
                        type="text"
                        value={editingName}
                        onChange={(event) => setEditingName(event.target.value)}
                        className="mr-3 flex-1 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 outline-none transition-all focus:ring-2 focus:ring-slate-800"
                      />
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleRenomear(espaco)}
                          disabled={saving || !editingName.trim()}
                          className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-emerald-600 transition-colors hover:bg-emerald-50 hover:text-emerald-700 disabled:cursor-not-allowed disabled:text-gray-400"
                        >
                          <Check size={16} />
                          Salvar
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelarEdicao}
                          className="rounded-xl px-3 py-2 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                        >
                          Cancelar
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <span className="font-medium text-slate-700">{espaco.nome}</span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleIniciarEdicao(espaco)}
                          className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-800"
                        >
                          <Pencil size={16} />
                          Renomear
                        </button>
                        <button
                          type="button"
                          onClick={() => handleExcluir(espaco)}
                          className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-red-500 transition-colors hover:bg-red-50 hover:text-red-700"
                        >
                          <Trash2 size={16} />
                          Excluir
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EspacosModal;
